<template>
  <div v-if="dataReady">
    <section class="scope-review-approvals">
      <k-panel
        :hasContentToggle="false"
        title="Approve Scope"
      >
        <template #body>
          <p>
            Once the scope has been reviewed and all feedback completed,
            complete the project or approve it for the creation phase where evidence can be uploaded.
          </p>
          <p>
            Approving the scope locks scope feedback: you will no longer be able to add text or upload a feedback document in New Feedback.
          </p>
          <p>
            <strong>Select 'Complete Project' if the project will be completed after Gateway.</strong>
          </p>
          <div v-if="!scopeApproved" class="button-container">
            <button class="btn btn-primary" @click="handleApproval('creation')" :disabled="isUpdating">
              Approve for Creation
            </button>
            <span>OR</span>
            <button class="btn btn-success" @click="handleApproval('complete')" :disabled="isUpdating">
              Complete Project
            </button>
          </div>
          <div v-else class="approved-container">
            <k-status-pill type="success" statusValue="approved">
              <template #label>
                <i class="fas fa-check" aria-hidden="true"></i> Project scope approved
              </template>
            </k-status-pill>
          </div>
        </template>
      </k-panel>
    </section>

    <section class="scope-review-content">
      <k-panel
        :hasContentToggle="true"
        :showContent="showScopeDetails"
        :title="`${projectData.name} Scope Details`"
        @toggle="showScopeDetails = $event"
        >
        <template #body>
          <section class="scope-review-content-item">
            <h3>Description</h3>
              <textarea :value="projectData.description" readonly></textarea>
          </section>
          <section class="scope-review-content-item">
            <h3>Scoping document</h3>
            <div class="content-box">
              <div v-if="projectData.scoping_document_link && !hasScopingDocFile">
                <a :href="projectData.scoping_document_link" target="_blank">
                  <i class="fas fa-external-link-alt" aria-hidden="true"></i> View Scoping Document
                </a>
              </div>
              <div v-else-if="projectData.scoping_document_link && hasScopingDocFile">
                <button class="btn btn-primary"
                  title="Download scoping document"
                  @click="downloadScopingDoc">
                  <i class="fas fa-download" aria-hidden="true"></i>
                  Download Scoping Document
                </button>
              </div>
              <p v-else>No scoping document provided</p>
            </div>
          </section>
          <section class="scope-review-content-item">
            <h3>Project Competencies</h3>
            <div class="content-box">
              <div class="competency-pills">
                <k-status-pill
                  v-for="competency in projectData.competencies"
                  :key="competency.id"
                  :status-value="competency.label"
                />
              </div>
            </div>
          </section>
          <section class="scope-review-content-item">
            <h3>Scoping feedback document</h3>
            <div class="content-box">
              <div v-if="projectData.feedback_document_link">
                <button class="btn btn-primary"
                  title="Download Latest Mentor Feedback"
                  @click="downloadScopingFeedbackDoc">
                  <i class="fas fa-download" aria-hidden="true"></i>
                  Download Latest Mentor Feedback
                </button>
              </div>
              <p v-else-if="scopeApproved">
                No scoping document feedback provided. Scope feedback is locked because the scope is approved.
              </p>
              <p v-else>No scoping document feedback provided. You can upload from New Feedback section.</p>
            </div>
          </section>
        </template>
      </k-panel>
      <section
        class="scope-feedback-section"
        :class="{ 'feedback-history-only': scopeApproved }"
      >
        <project-review-feedback
          ref="scopeFeedback"
          :project-data="projectData"
          feedback-type="scope"
        />

        <k-panel v-if="!scopeApproved" :hasContentToggle="false" title="New Feedback">
          <template #body>
            <k-text-editor-with-file-upload
              ref="mentorFeedbackEditor"
              textLabel="Scope Feedback"
              v-model="currentScopeFeedbackText"
              :textMaxLength="1024"
              :acceptedFormats="uploadFileAcceptedFormats"
              :require-text="true"
              dropfield-id="scope-feedback-upload"
              @file-selected="onScopeFileSelected"
              @save="saveScopeFeedback"
            />
          </template>
        </k-panel>

      </section>
    </section>
  </div>
</template>

<style scoped>
.button-container,
.approved-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.scope-review-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.scope-review-content-item {
  margin-top: 20px;
}

.scope-review-content-item .content-box {
  padding: 16px;
  border: 1px solid var(--kate-type-alt-dark);
  border-radius: 8px;
  margin-top: 10px;
}

.scope-review-content-item h3 {
  margin-bottom: 10px;
}

.competency-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

textarea {
  width: 100%;
  height: 100px;
  padding: 10px;
  border: 1px solid var(--kate-type-alt-dark);
  border-radius: 8px;
  background-color: transparent;
}

.scope-feedback-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.scope-feedback-section.feedback-history-only {
  grid-template-columns: 1fr;
}

@media (max-width: 768px) {
  .scope-feedback-section {
    grid-template-columns: 1fr;
  }
}
</style>
<script>
import KPanel from '@base-components/k-panel.vue';
import KStatusPill from '@base-components/k-status-pill.vue';
import KTextEditorWithFileUpload from '../../../../components/k-text-editor-with-file-upload.vue';
import ProjectReviewFeedback from './project-review-feedback.vue';

export default {
  name: 'ProjectReviewScope',
  props: {
    projectData: {
      type: Object,
      default: null,
    },
  },
  components: {
    KPanel,
    ProjectReviewFeedback,
    KStatusPill,
    KTextEditorWithFileUpload,
  },

  data() {
    return {
      isUpdating: false,
      showScopeDetails: true,
      currentScopeFeedbackText: '',
      scopeFeedbackFile: null,
    };
  },

  computed: {
    dataReady() {
      return !!this.projectData;
    },
    scopeApproved() {
      return this.projectData.scoping_completed;
    },

    hasScopingDocFile() {
      return this.projectData.scoping_document_link?.includes('/scoping_doc/');
    },

    uploadFileAcceptedFormats() {
      const formats = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX files
        'application/vnd.ms-powerpoint', // PPT files
      ];
      return formats.join(',');
    },

    projectPhase() {
      return 'scoping';
    },
  },

  methods: {
    async syncEvidenceMappings() {
      try {
        // Fetch current evidence for the project
        const res = await this.$http.get(`/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence`);
        const list = Array.isArray(res.data) ? res.data : [];
        const requests = list.map(ev => {
          const mappingIds = (ev.competency_mappings || []).map(m => m.programme_competency_id).filter(Boolean);
          const payload = {
            programme_id: this.projectData.programme_id,
            name: ev.name || '',
            description: ev.description ?? null,
            approved: Boolean(ev.approved),
            project_competency_mapping_ids: mappingIds,
          };
          return this.$http.put(
            `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/evidence/${ev.project_evidence_id}`,
            payload,
          );
        });
        await Promise.allSettled(requests);
      } catch (err) {
        this.$logger.autowarn('Error syncing evidence mappings after scope approval', { projectId: this.projectData.id }, err);
      }
    },

    getToastMessage(action) {
      const lockNotice = ' After approval, scope feedback will be locked and you will not be able to add further text or uploads.';
      if (action === 'creation') {
        return `Please confirm you wish to approve the scope and move the project into creation phase.${lockNotice}`;
      }
      return `Please confirm you wish to mark the project as completed.${lockNotice}`;
    },

    handleApproval(action) {
      const payload = {
        programme_id: this.projectData.programme_id,
        name: this.projectData.name,
        description: this.projectData.description,
        project_type: 'portfolio',
      };

      if (action === 'creation') {
        payload.status = 'in_progress';
        payload.scoping_completed = true;
        payload.project_completed = false;
      } else {
        payload.status = 'completed';
        payload.scoping_completed = true;
        payload.project_completed = true;
      }
      this.$ktoast.confirmToast(this.getToastMessage(action), 'warning', () => this.approveScope(payload), undefined);
    },

    async approveScope(payload) {
      this.isUpdating = true;
      this.$logger.info('Approving project scope', { projectId: this.projectData.id, status: payload.status });
      try {
        await this.$http.put(`/api/curriculum/admin/programmes/projects/${this.projectData.id}/learner/${this.projectData.user_id}`, payload);
        // If scope action resulted in completion, resubmit evidence mappings to preserve them
        // Do this asynchronously so we don't block the success UX
        if (payload.project_completed) {
          this.syncEvidenceMappings().catch(() => {});
        }
        this.$ktoast.success('Project scope approved', { projectId: this.projectData.id, status: payload.status });
        this.$logger.info('Project scope approved', { projectId: this.projectData.id, status: payload.status });
        // Reload project data to reflect approval status changes
        this.$emit('project-updated');
      } catch (err) {
        this.$logger.autowarn('Error approving project scope', { projectId: this.projectData.id, status: payload.status }, err);
        this.$ktoast.warning('Error approving project scope', err);
      } finally {
        this.isUpdating = false;
      }
    },

    downloadScopingDoc() {
      if (this.projectData.scoping_document_link && this.hasScopingDocFile) {
        const filename = this.projectData.scoping_document_link.split('/').pop();
        const downloadUrl = `/api/curriculum/programmes/projects/${this.projectData.id}/doc/${filename}`;
        window.open(downloadUrl, '_blank');
      }
    },

    downloadScopingFeedbackDoc() {
      if (this.projectData.feedback_document_link) {
        const filename = this.projectData.feedback_document_link.split('/').pop();
        const downloadUrl = `/api/curriculum/programmes/projects/${this.projectData.id}/scoping-feedback/${filename}`;
        window.open(downloadUrl, '_blank');
      }
    },

    onScopeFileSelected(file) {
      this.scopeFeedbackFile = file[0] || null;
    },

    async uploadScopeFeedbackFile() {
      if (!this.scopeFeedbackFile) return null;
      const formData = new FormData();
      formData.append('file', this.scopeFeedbackFile);
      return this.$http.post(
        `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/scoping-feedback/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },

    async submitScopeFeedbackText() {
      return this.$http.put(
        `/api/curriculum/admin/programmes/${this.projectData.programme_id}/projects/${this.projectData.id}/learner/${this.projectData.user_id}/feedback`,
        {
          feedback_text: this.currentScopeFeedbackText,
          project_phase_at_feedback: this.projectPhase,
        },
      );
    },

    async saveScopeFeedback() {
      this.$logger.info('Adding feedback', { projectId: this.projectData.id });
      this.isUpdating = true;
      try {
        await this.uploadScopeFeedbackFile();
        const response = await this.submitScopeFeedbackText();
        this.$logger.info('Successfully added feedback', { projectId: this.projectData.id });
        this.$ktoast.success('Feedback added successfully');
        this.resetScopeFeedbackInputs();
        if (this.$refs.scopeFeedback) {
          await this.$refs.scopeFeedback.getFeedback();
        }
        return response;
      } catch (error) {
        this.$logger.error('Error adding feedback', { projectId: this.projectData.id }, error);
        this.$ktoast.showError(error, true);
        return null; // if it fails
      } finally {
        this.isUpdating = false;
      }
    },

    resetScopeFeedbackInputs() {
      this.currentScopeFeedbackText = '';
      this.scopeFeedbackFile = null;
      if (this.$refs.mentorFeedbackEditor) {
        this.$refs.mentorFeedbackEditor.reset();
      }
    },
  },
};
</script>
